// src/components/Hero.jsx
import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import shepherdLogo from '../assets/Shepherd_blue_logo.png';
import shopifyLogo from '../assets/shopify-logo.png';
import claudeLogo from '../assets/claude-logo.png';
import xeroLogo from '../assets/xero-logo.png';
import klaviyoLogo from '../assets/klaviyo-logo.png';
import chromeLogo from '../assets/chrome-logo.png';
import shepherdHero from '../assets/shepherdHeroVid.mp4';
import multilogo from '../assets/multilogo.png';

export default function Hero() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 968);
  const [currentCompanies, setCurrentCompanies] = useState(0);

  const companies = [
    { name: "Shopify", logo: shopifyLogo },
    { name: "Klaviyo", logo: klaviyoLogo },
    { name: "Claude", logo: claudeLogo },
    { name: "Xero", logo: xeroLogo },
    { name: "And More!", logo: multilogo }
  ];

  useEffect(() => {
    // 1. Handle Window Resizing
    const handleResize = () => setIsMobile(window.innerWidth < 968);
    window.addEventListener('resize', handleResize);

    // 2. Logo Animation Timer
    const timer = setInterval(() => {
      setCurrentCompanies((prev) => (prev + 1) % companies.length);
    }, 2000);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearInterval(timer);
    };
  }, [companies.length]);

  // Dynamic Layout Adjustments
  const responsiveInner = {
    ...styles.inner,
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1.15fr',
    textAlign: isMobile ? 'center' : 'left',
    gap: isMobile ? '2rem' : '4rem',
  };

  const responsiveLeft = {
    ...styles.left,
    alignItems: isMobile ? 'center' : 'flex-start',
  };

  return (
    <section style={{...styles.hero, top: isMobile ? 80 : 140}}>
      <div style={responsiveInner}>

        {/* LEFT: text content */}
        <div style={responsiveLeft}>
          <h1 style={{...styles.h1, textAlign: isMobile ? 'center' : 'left'}}>
            Guiding you through software products like&nbsp;
            <span style={styles.rollContainer}>
              <span 
                key={currentCompanies} 
                className="roll-down-animation" 
                style={styles.accent}
              >
                <img src={companies[currentCompanies].logo} alt={companies[currentCompanies].name} style={{ height: "100%" }} />
                {companies[currentCompanies].name}
              </span>
            </span>
          </h1>

          <p style={{...styles.sub, textAlign: isMobile ? 'center' : 'left', margin: isMobile ? '0 auto 2rem' : '0 0 2rem'}}>
            Shepherd guides you through software with in-app step-by-step guidance built by creators in the community, so you know how to use software from the very start.
          </p>

          <div style={{...styles.actions, justifyContent: isMobile ? 'center' : 'flex-start'}}>
            <a
              href="https://chromewebstore.google.com/detail/shepherd-build-or-follow/okjbbpkkanbhfpmmafpmijcekpjjcncl"
              target="_blank" rel="noreferrer"
              style={styles.btnPrimary}
            >
              <img src={chromeLogo} alt="Google Chrome Logo" style={{width: "30px", height: "30px" }} />
              Install for Chrome - It's free
            </a>
          </div>
        </div>

        {/* RIGHT: browser mockup */}
        <div style={styles.right}>
          <div style={{...styles.browser, maxWidth: isMobile ? '100%' : '640px'}}>
            <div style={styles.bar}>
              <div style={styles.dots}>
                <div style={{ ...styles.dot, background: '#fc5454' }} />
                <div style={{ ...styles.dot, background: '#ffbe2e' }} />
                <div style={{ ...styles.dot, background: '#2dc254' }} />
              </div>
              <div style={styles.url}>admin.shopify.com/store</div>
            </div>

            <div style={styles.videoContainer}>
              <video autoPlay muted loop playsInline style={styles.video}>
                <source src={shepherdHero} type="video/mp4" />
              </video>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

const styles = {
  hero: {
    position: 'relative', overflow: 'hidden',
    top: 140, left: 0, right: 0,
    display: 'flex', alignItems: 'center',
    padding: '0rem clamp(1.5rem, 5vw, 5rem) 5rem',
    background: 'radial-gradient(ellipse 60% 70% at 70% 50%, rgba(26,115,232,0.07) 0%, transparent 70%), var(--warm-white)',
  },
  inner: {
    maxWidth: "100vw", margin: '0 auto', width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr 1.15fr',
    gap: '4rem',
    alignItems: 'center',
  },
  left: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
    animation: 'fadeUp 0.6s ease both',
  },
  right: {
    animation: 'fadeUp 0.8s 0.2s ease both',
  },
  h1: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 'clamp(2.2rem, 4vw, 3.8rem)',
    fontWeight: 700, lineHeight: 1.1,
    color: 'var(--charcoal)',
    marginBottom: '1.25rem',
    textAlign: 'left',
  },
  rollContainer: {
    display: 'inline-flex',
    position: 'relative',
    overflow: 'hidden',
    verticalAlign: 'bottom',
    height: '1.2em', 
    perspective: '1000px',
  },
  accent: { 
    color: 'var(--brand)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    transformOrigin: '50% 50% -25px', 
  },
  sub: {
    fontSize: 'clamp(0.95rem, 1.4vw, 1.1rem)',
    color: 'var(--text-muted)', maxWidth: 420,
    marginBottom: '2rem', fontWeight: 300, lineHeight: 1.7,
    textAlign: 'left',
  },
  actions: {
    display: 'flex', alignItems: 'center', gap: '1rem',
    flexWrap: 'wrap', marginBottom: '2.5rem',
  },
  btnPrimary: {
    width: "100%",
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'var(--brand)', color: 'white',
    padding: '13px 26px', borderRadius: 'var(--radius-md)',
    fontSize: '1.15rem', fontWeight: 500,
    textDecoration: 'none', cursor: 'pointer',
    transition: 'background 0.2s, transform 0.15s',
  },
  btnSecondary: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'transparent', color: 'var(--text-mid)',
    padding: '13px 22px', borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem', fontWeight: 400,
    textDecoration: 'none', border: '1px solid var(--border-mid)',
    cursor: 'pointer',
  },
  right: {
    animation: 'fadeUp 0.8s 0.2s ease both',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  browser: {
    width: '100%',
    maxWidth: '640px', // Slightly wider since there's no popup crowding it
    background: 'white', 
    borderRadius: '12px',
    border: '1px solid var(--border)', 
    overflow: 'hidden',
    boxShadow: '0 32px 80px rgba(26,31,54,0.12), 0 4px 16px rgba(26,31,54,0.06)',
  },
  bar: {
    background: '#f1f3f4', 
    padding: '10px 16px',
    display: 'flex', 
    alignItems: 'center', 
    gap: 10,
    borderBottom: '1px solid #ddd',
  },
  dots: { display: 'flex', gap: 6 },
  dot: { width: 10, height: 10, borderRadius: '50%' },
  url: {
    flex: 1, 
    background: 'white', 
    borderRadius: 6,
    padding: '4px 10px', 
    fontSize: '0.75rem', 
    color: 'var(--text-muted)',
    border: '1px solid #ddd', 
    textAlign: 'center',
    fontFamily: 'sans-serif',
  },
 videoContainer: {
    width: '100%',
    aspectRatio: '16 / 9', // 1. ADD THIS: Locks the container to a standard video ratio
    lineHeight: 0,
    background: '#000',
    display: 'block',
    overflow: 'hidden',
  },
  video: {
    width: '100%',
    height: '100%',       // 2. CHANGE TO 100%: Fills the aspect-ratio container
    display: 'block',
    /* "cover" fills the box (might crop edges slightly, but NO distortion)
       "contain" shows the full video with black bars (letterboxing) 
    */
    objectFit: 'cover',   
  },
};
